import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useProximosVencimientos } from '../../hooks/useProximosVencimientos';
import { useStackedTable } from '../../hooks/useStackedTable';
import { MONEDA_TITULO } from '../../constants/monedas';
import { formatSaldo } from '../../utils/formatters';
import { fromIsoDate } from '../../utils/dateHelpers';
import {
  detalleDollarLinked, esDollarLinked, formatIsoDay, formatMontoEvento,
} from '../../utils/acreditacionesEsperadas';
import {
  HORIZONTES_DIAS,
  HORIZONTE_DEFAULT,
  SENTIDO_COBROS,
  SENTIDO_PAGOS,
  SENTIDO_TODOS,
  TIPO_CAUCION,
  TIPO_PASE,
  filtrarEventos,
  filtrarHorizonte,
  resumirPorMoneda,
  sumarDiasIso,
} from '../../utils/proximosVencimientos';

// Una cartera grande en 90 días son cientos de filas: se muestran de a tanto.
const LIMITE_FILAS = 100;
const COLOR_COBRO = '#065F46';
const COLOR_PAGO = '#991B1B';

const SENTIDOS = [
  { key: SENTIDO_TODOS, label: 'Todos' },
  { key: SENTIDO_COBROS, label: 'A cobrar' },
  { key: SENTIDO_PAGOS, label: 'A pagar' },
];

const Section = styled.section`
  margin-top: 2.5rem;
  font-family: ${props => props.theme.typography.fontFamily};
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 0.6rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.colors.primary};
`;
const Title = styled.h3`
  margin: 0;
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.secondary};
`;
const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
`;
const Segmented = styled.div`
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.25rem;
  background-color: rgba(26, 40, 62, 0.08);
  border-radius: 10px;
`;
const SegmentButton = styled.button`
  min-height: 34px;
  padding: 0 0.8rem;
  border: none;
  border-radius: 8px;
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  background-color: ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.$active ? props.theme.colors.textPrimary : props.theme.colors.secondary)};
  @media (max-width: 768px) {
    min-height: 44px;
  }
`;
const Aviso = styled.div`
  margin-bottom: 1rem;
  padding: 0.6rem 0.9rem;
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.warning};
  color: ${props => props.theme.colors.secondary};
  font-size: 0.82rem;
`;
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
`;
// Un <button> centra su contenido en vertical: sin el flex, las tarjetas más bajas
// quedan con el texto flotando al medio al estirarse a la altura de la fila.
const KpiCard = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
  font-family: inherit;
  background-color: ${props => props.theme.colors.surface};
  border: 2px solid ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
  padding: 1rem 1.25rem;
  cursor: pointer;
  min-width: 0;
`;
const KpiHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.6rem;
  font-family: ${props => props.theme.typography.titleFamily};
  font-weight: 700;
  color: ${props => props.theme.colors.secondary};
  small { font-family: ${props => props.theme.typography.fontFamily}; font-weight: 500; color: ${props => props.theme.colors.textSecondary}; }
`;
const KpiLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.2rem 0;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  strong { color: ${props => props.$color || props.theme.colors.secondary}; white-space: nowrap; }
  ${props => props.$total && `
    margin-top: 0.3rem;
    padding-top: 0.45rem;
    border-top: 1px solid ${props.theme.colors.border};
  `}
`;
const KpiNota = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.75rem;
  color: ${props => (props.$destacada ? props.theme.colors.secondary : props.theme.colors.textSecondary)};
`;
const Filtros = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
`;
const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.85rem;
  min-width: 300px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
  @media (max-width: 768px) {
    min-height: 44px;
  }
`;
const ChipMoneda = styled.button`
  border: 1px solid ${props => props.theme.colors.primary};
  background-color: rgba(26, 40, 62, 0.08);
  color: ${props => props.theme.colors.primary};
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
`;
const TableWrapper = styled.div`
  overflow-x: auto;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th, td {
    padding: 0.65rem 0.9rem;
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    vertical-align: top;
  }
  th {
    font-family: ${props => props.theme.typography.titleFamily};
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${props => props.theme.colors.textSecondary};
    white-space: nowrap;
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background-color: ${props => props.theme.colors.background}; }
  small { display: block; font-size: 0.74rem; color: ${props => props.theme.colors.textSecondary}; }
`;
const TipoBadge = styled.span`
  display: inline-block;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
  background-color: rgba(26, 40, 62, 0.1);
  color: ${props => props.theme.colors.primary};
`;
const Monto = styled.span`
  display: block;
  text-align: right;
  font-weight: 700;
  white-space: nowrap;
  color: ${props => props.$color};
`;
const Empty = styled.p`
  margin: 0;
  padding: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
`;
const VerTodos = styled.button`
  display: block;
  margin: 0.75rem auto 0;
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.secondary};
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;
const Leyenda = styled.ul`
  margin: 0.75rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.76rem;
  line-height: 1.5;
  color: ${props => props.theme.colors.textSecondary};
  strong { color: ${props => props.theme.colors.secondary}; }
`;

const esPagare = (ev) => /^PAGAR/i.test(ev.instrumento);

const etiquetaTipo = (ev) => {
  if (ev.tipo === TIPO_CAUCION) return 'Caución';
  if (ev.tipo === TIPO_PASE) return 'Pase';
  if (ev.tipo === 'cheque') return esPagare(ev) ? 'Pagaré' : 'Cheque';
  if (ev.tipo === 'chequePropio') return esPagare(ev) ? 'Pagaré propio' : 'Cheque propio';
  return 'Renta fija';
};

const etiquetaDia = (iso, hoy) => {
  if (iso === hoy) return 'Hoy';
  if (iso === sumarDiasIso(hoy, 1)) return 'Mañana';
  return fromIsoDate(iso).toLocaleDateString('es-AR', { weekday: 'long' });
};

const colorDe = (ev) => ((ev.monto ?? ev.montoEstimado ?? ev.montoNominal ?? 0) < 0 ? COLOR_PAGO : COLOR_COBRO);

const avisosDeEstado = ({ meta, fechaHoy, hastaSeleccionado, eventosOmitidos }) => {
  if (!meta) {
    return ['Todavía no hay proyección del SGC (cupones, amortizaciones, cheques y pagarés): se muestran solo cauciones y pases.'];
  }
  const avisos = [];
  if (meta.fecha && meta.fecha < fechaHoy) {
    avisos.push(`La proyección del SGC se calculó el ${formatIsoDay(meta.fecha)}: puede no reflejar compras o ventas posteriores.`);
  }
  if (meta.hasta && meta.hasta < hastaSeleccionado) {
    avisos.push(`La proyección del SGC llega hasta el ${formatIsoDay(meta.hasta)}.`);
  }
  if (eventosOmitidos > 0) {
    avisos.push(`Hay ${eventosOmitidos} vencimiento(s) más que no se muestran: clientes con demasiados instrumentos en el período.`);
  }
  return avisos;
};

const ResumenMoneda = ({ resumen, activa, onClick }) => {
  const {
    bucket, cantidad, cobros, pagos, neto, hayParcial, linkedNominal, linkedCantidad, linkedEstimado, linkedSinEstimar,
  } = resumen;
  return (
    <KpiCard
      type="button"
      $active={activa}
      aria-pressed={activa}
      onClick={onClick}
      title={activa ? 'Mostrar todas las monedas' : `Ver solo ${MONEDA_TITULO[bucket]}`}
    >
      <KpiHeader>
        {MONEDA_TITULO[bucket]}
        <small>{cantidad} {cantidad === 1 ? 'vencimiento' : 'vencimientos'}</small>
      </KpiHeader>
      {cantidad === 0 ? (
        <KpiNota>Sin vencimientos en el período.</KpiNota>
      ) : (
        <>
          <KpiLine $color={COLOR_COBRO}><span>A cobrar</span><strong>{formatSaldo(cobros, bucket)}</strong></KpiLine>
          <KpiLine $color={COLOR_PAGO}><span>A pagar</span><strong>{formatSaldo(pagos, bucket)}</strong></KpiLine>
          <KpiLine $total>
            <span>Neto</span>
            <strong title={hayParcial ? 'Incluye montos mínimos (sin intereses)' : undefined}>
              {hayParcial ? '≈ ' : ''}{formatSaldo(neto, bucket)}
            </strong>
          </KpiLine>
          {linkedCantidad > 0 && (
            <KpiNota $destacada>
              Además, dollar linked por {formatSaldo(linkedNominal, 'USD')} nominales que se acreditan en pesos
              {linkedSinEstimar < linkedCantidad
                ? <>: ≈ {formatSaldo(linkedEstimado, 'ARS')} estimados al dólar mayorista{linkedSinEstimar > 0 ? ' (algunos sin estimar)' : ''}</>
                : ' al A3500 del día de pago'}
              . No suman al neto.
            </KpiNota>
          )}
        </>
      )}
    </KpiCard>
  );
};

/**
 * Próximos vencimientos de los clientes, por moneda: cupones, amortizaciones, letras
 * y T-Bills, cheques y pagarés MAV (proyección del SGC) y cauciones y pases tomadores
 * de la planilla. El productor ve sus cuentas; un comercial, las de `alcance` (o toda
 * la casa con `alcance` null, como su Dashboard).
 */
const ProximosVencimientosSection = ({ alcance = null }) => {
  const { eventos, meta, fechaHoy, acotado, eventosOmitidos, isLoading, error } = useProximosVencimientos(alcance);
  const [horizonte, setHorizonte] = useState(HORIZONTE_DEFAULT);
  const [moneda, setMoneda] = useState(null);
  const [sentido, setSentido] = useState(SENTIDO_TODOS);
  const [busqueda, setBusqueda] = useState('');
  const [verTodos, setVerTodos] = useState(false);
  const stackedTable = useStackedTable({ title: 'Cliente', primary: ['Fecha', 'Detalle', 'Moneda', 'Monto'] });

  const enHorizonte = useMemo(
    () => filtrarHorizonte(eventos, fechaHoy, horizonte),
    [eventos, fechaHoy, horizonte],
  );
  // Las tarjetas por moneda respetan búsqueda y sentido, pero no el filtro de moneda.
  const sinFiltroMoneda = useMemo(
    () => filtrarEventos(enHorizonte, { sentido, busqueda }),
    [enHorizonte, sentido, busqueda],
  );
  const resumen = useMemo(() => resumirPorMoneda(sinFiltroMoneda), [sinFiltroMoneda]);
  const filas = useMemo(
    () => (moneda ? sinFiltroMoneda.filter(ev => ev.bucket === moneda) : sinFiltroMoneda),
    [sinFiltroMoneda, moneda],
  );
  const visibles = verTodos ? filas : filas.slice(0, LIMITE_FILAS);
  const hastaSeleccionado = sumarDiasIso(fechaHoy, horizonte);
  const avisos = isLoading ? [] : avisosDeEstado({ meta, fechaHoy, hastaSeleccionado, eventosOmitidos });

  const renderCuerpo = () => {
    if (isLoading) return <Empty>Cargando vencimientos...</Empty>;
    if (error && eventos.length === 0) {
      return <Empty>No se pudieron cargar los vencimientos. Probá recargar la página.</Empty>;
    }
    if (filas.length === 0) {
      return <Empty>Sin vencimientos hasta el {formatIsoDay(hastaSeleccionado)} con estos filtros.</Empty>;
    }
    return (
      <Table ref={stackedTable.ref} className={stackedTable.className}>
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Cliente</th>
            <th>Tipo</th>
            <th>Detalle</th>
            <th>Moneda</th>
            <th style={{ textAlign: 'right' }}>Monto</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map(ev => (
            <tr key={ev.id}>
              <td style={{ whiteSpace: 'nowrap' }}>
                {formatIsoDay(ev.fecha)}
                <small>{etiquetaDia(ev.fecha, fechaHoy)}</small>
              </td>
              <td>
                {ev.cliente || '—'}
                <small>{ev.nroComitente}</small>
              </td>
              <td><TipoBadge>{etiquetaTipo(ev)}</TipoBadge></td>
              <td>
                {ev.concepto}
                <small>{ev.instrumento}</small>
                {ev.fechaOriginal && <small>Fecha informada {formatIsoDay(ev.fechaOriginal)}, corrida al día hábil.</small>}
                {esDollarLinked(ev) ? <small>{detalleDollarLinked(ev)}</small> : ev.nota && <small>{ev.nota}</small>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {MONEDA_TITULO[ev.bucket] || ev.bucket}
                {esDollarLinked(ev) && <small>dollar linked</small>}
              </td>
              <td><Monto $color={colorDe(ev)}>{formatMontoEvento(ev, ev.bucket)}</Monto></td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <Section aria-labelledby="proximos-vencimientos-titulo">
      <Header>
        <div>
          <Title id="proximos-vencimientos-titulo">
            {acotado ? 'Próximos vencimientos de tus clientes' : 'Próximos vencimientos de los clientes'}
          </Title>
          <Subtitle>
            Cupones, amortizaciones, cheques y pagarés
            {meta?.fechaCartera ? ` (cartera del SGC al ${formatIsoDay(meta.fechaCartera)})` : ''}, más
            cauciones y pases tomadores.
          </Subtitle>
        </div>
        <Segmented role="group" aria-label="Horizonte" className="axon-scroll-x">
          {HORIZONTES_DIAS.map(dias => (
            <SegmentButton
              key={dias}
              type="button"
              $active={horizonte === dias}
              aria-pressed={horizonte === dias}
              onClick={() => {
                setHorizonte(dias);
                setVerTodos(false);
              }}
            >
              {dias} días
            </SegmentButton>
          ))}
        </Segmented>
      </Header>

      {avisos.map(texto => <Aviso key={texto} role="status">{texto}</Aviso>)}

      {!isLoading && (
        <KpiGrid>
          {resumen.map(r => (
            <ResumenMoneda
              key={r.bucket}
              resumen={r}
              activa={moneda === r.bucket}
              onClick={() => setMoneda(prev => (prev === r.bucket ? null : r.bucket))}
            />
          ))}
        </KpiGrid>
      )}

      <Filtros>
        <SearchInput
          className="axon-fill"
          type="search"
          placeholder="Buscar cliente o instrumento..."
          aria-label="Buscar vencimientos"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
        <Segmented role="group" aria-label="Sentido">
          {SENTIDOS.map(({ key, label }) => (
            <SegmentButton
              key={key}
              type="button"
              $active={sentido === key}
              aria-pressed={sentido === key}
              onClick={() => setSentido(key)}
            >
              {label}
            </SegmentButton>
          ))}
        </Segmented>
        {moneda && (
          <ChipMoneda
            type="button"
            onClick={() => setMoneda(null)}
            title="Quitar filtro de moneda"
            aria-label={`Quitar filtro ${MONEDA_TITULO[moneda]}`}
          >
            {MONEDA_TITULO[moneda]} ✕
          </ChipMoneda>
        )}
      </Filtros>

      <TableWrapper>{renderCuerpo()}</TableWrapper>

      {!verTodos && filas.length > LIMITE_FILAS && (
        <VerTodos type="button" onClick={() => setVerTodos(true)}>
          Mostrar los {filas.length} vencimientos
        </VerTodos>
      )}

      <Leyenda>
        <li>
          <strong>≥</strong> monto mínimo: letras sin cronograma del proveedor (solo el capital) y cauciones o
          pases tomadores (sin los intereses). <strong>≈</strong> total que incluye montos mínimos.
        </li>
        <li>
          <strong>Dollar linked</strong>: nominal en dólares que se acredita en pesos al tipo de cambio A3500 del
          día de pago. <strong>≈</strong> en la fila: monto en pesos estimado al dólar mayorista.
        </li>
        <li>
          Información de referencia: la acreditación efectiva depende de la liquidación de cada instrumento.
        </li>
      </Leyenda>
    </Section>
  );
};

export default ProximosVencimientosSection;
